import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft } from 'lucide-react';

interface LegalPagesProps {
  type: 'terms' | 'privacy';
  onBack: () => void;
}

export default function LegalPages({ type, onBack }: LegalPagesProps) {
  const isTerms = type === 'terms';

  return (
    <div className="min-h-screen w-full bg-background p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isTerms ? 'Conditions d\'utilisation' : 'Politique de confidentialité'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="prose prose-sm max-w-none">
            {isTerms ? (
              <>
                <h3>1. Acceptation des conditions</h3>
                <p>
                  En utilisant l'application Signalement Sangliers France, vous acceptez d'être lié par ces conditions d'utilisation. 
                  Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre service.
                </p>

                <h3>2. Description du service</h3>
                <p>
                  Signalement Sangliers France est une plateforme permettant aux citoyens de signaler la présence de sangliers 
                  sur le territoire français. L'objectif est de créer une cartographie collaborative pour améliorer la sécurité 
                  et la gestion des populations de sangliers.
                </p>

                <h3>3. Utilisation du service</h3>
                <p>
                  Vous vous engagez à utiliser le service de manière responsable et légale. Les signalements doivent être 
                  véridiques et basés sur des observations réelles. Tout signalement frauduleux ou malveillant peut entraîner 
                  la suspension de votre compte.
                </p>

                <h3>4. Responsabilité</h3>
                <p>
                  Les informations fournies par les utilisateurs sont de leur seule responsabilité. Nous ne garantissons pas 
                  l'exactitude des signalements et déclinons toute responsabilité en cas de dommages résultant de l'utilisation 
                  de ces informations.
                </p>

                <h3>5. Propriété intellectuelle</h3>
                <p>
                  Le contenu de l'application, à l'exception des signalements utilisateurs, est protégé par les droits de 
                  propriété intellectuelle. Toute reproduction non autorisée est interdite.
                </p>

                <h3>6. Modification des conditions</h3>
                <p>
                  Nous nous réservons le droit de modifier ces conditions à tout moment. Les utilisateurs seront informés 
                  des changements significatifs par email ou notification dans l'application.
                </p>

                <h3>7. Contact</h3>
                <p>
                  Pour toute question concernant ces conditions d'utilisation, vous pouvez nous contacter à l'adresse : 
                  contact@signalement-sangliers-france.fr
                </p>
              </>
            ) : (
              <>
                <h3>1. Collecte des données</h3>
                <p>
                  Nous collectons uniquement les données nécessaires au fonctionnement du service : votre adresse email, 
                  nom, prénom, et les données de géolocalisation de vos signalements. Aucune donnée sensible n'est collectée 
                  sans votre consentement explicite.
                </p>

                <h3>2. Utilisation des données</h3>
                <p>
                  Vos données personnelles sont utilisées pour :
                </p>
                <ul>
                  <li>Créer et gérer votre compte utilisateur</li>
                  <li>Traiter vos signalements de sangliers</li>
                  <li>Vous envoyer des notifications importantes</li>
                  <li>Améliorer notre service</li>
                </ul>

                <h3>3. Partage des données</h3>
                <p>
                  Nous ne vendons, ne louons et ne partageons jamais vos données personnelles avec des tiers à des fins 
                  commerciales. Les données de signalement peuvent être partagées de manière anonymisée avec les autorités 
                  compétentes pour la gestion de la faune.
                </p>

                <h3>4. Géolocalisation</h3>
                <p>
                  Les données de géolocalisation sont collectées uniquement lors de la création d'un signalement et avec 
                  votre autorisation. Ces données sont essentielles pour la cartographie des zones de présence de sangliers.
                </p>

                <h3>5. Sécurité des données</h3>
                <p>
                  Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger 
                  vos données contre tout accès non autorisé, altération, divulgation ou destruction.
                </p>

                <h3>6. Vos droits</h3>
                <p>
                  Conformément au RGPD, vous disposez des droits suivants :
                </p>
                <ul>
                  <li>Droit d'accès à vos données</li>
                  <li>Droit de rectification</li>
                  <li>Droit à l'effacement</li>
                  <li>Droit à la portabilité</li>
                  <li>Droit d'opposition</li>
                </ul>

                <h3>7. Conservation des données</h3>
                <p>
                  Vos données personnelles sont conservées tant que votre compte est actif. Les données de signalement 
                  peuvent être conservées de manière anonymisée à des fins statistiques et scientifiques.
                </p>

                <h3>8. Contact</h3>
                <p>
                  Pour exercer vos droits ou pour toute question relative à cette politique de confidentialité, 
                  contactez-nous à : privacy@signalement-sangliers-france.fr
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}